import React, { useState, useEffect, useCallback } from "react";
import { useSettings } from "../../context/SettingsContext";
import Card from "../card/Card";

/* ─── Countdown helper ───────────────────────────────────── */
const pad = (n) => String(n).padStart(2, "0");

function calcTimeLeft(endDate) {
  if (!endDate) return null;
  const diff = new Date(endDate) - new Date();
  if (diff <= 0) return null;
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
}

/* ─── Single digit tile ──────────────────────────────────── */
function TimerTile({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-red-600 text-white rounded-md w-9 h-9 flex items-center justify-center font-black text-base tabular-nums shadow-sm">
        {pad(value)}
      </div>
      <span className="text-[9px] text-gray-500 font-medium mt-0.5 uppercase tracking-wide">
        {label}
      </span>
    </div>
  );
}

function PopularProduct({ products, title, description, isFlashSale = false }) {
  const settings = useSettings();
  const endDate = settings?.flash_sale_end_date;
  const startDate = settings?.flash_sale_start_date;

  const getTime = useCallback(() => calcTimeLeft(endDate), [endDate]);
  const [timeLeft, setTimeLeft] = useState(getTime);

  useEffect(() => {
    if (!isFlashSale || !endDate) return;
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(endDate)), 1000);
    return () => clearInterval(timer);
  }, [endDate, isFlashSale]);

  /* Check the sale window is open right now */
  const isActive = (() => {
    if (!isFlashSale) return true;
    if (!endDate) return false;
    const now = new Date();
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return false;
    if (startDate) {
      const start = new Date(startDate);
      if (!isNaN(start.getTime()) && now < start) return false;
    }
    return now <= end;
  })();

  /* Hide entire section if flash sale is inactive, expired, or has no products */
  if (isFlashSale && (!isActive || !timeLeft || !products || products.length === 0)) {
    return null;
  }

  return (
    <div
      id={isFlashSale ? "flash-sale" : "discount"}
      className="bg-gray-50 py-5 sm:py-8 lg:py-10 mx-auto max-w-screen-2xl px-3 sm:px-10"
    >
      <div className="mb-6 sm:mb-10 flex justify-center">
        <div className="text-center w-full max-w-3xl flex flex-col items-center">
          {isFlashSale ? (
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mb-4">
              <div className="flex flex-wrap items-center justify-center gap-3">
                {/* Lightning Bolt Icon */}
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-red-600 text-white flex-shrink-0">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                <h2 className="text-xl lg:text-2xl text-black font-bold">
                  {title}
                </h2>
                {/* Live Badge */}
                <span className="flex items-center gap-1 bg-red-50 border border-red-200 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping inline-block" />
                  Live
                </span>
              </div>

              {/* Flash Sale Timer */}
              {timeLeft && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 font-medium mr-1">Ends in:</span>
                  {timeLeft.d > 0 && (
                    <>
                      <TimerTile value={timeLeft.d} label="day" />
                      <span className="text-red-500 font-black text-lg mb-4 leading-none">:</span>
                    </>
                  )}
                  <TimerTile value={timeLeft.h} label="hr" />
                  <span className="text-red-500 font-black text-lg mb-4 leading-none">:</span>
                  <TimerTile value={timeLeft.m} label="min" />
                  <span className="text-red-500 font-black text-lg mb-4 leading-none">:</span>
                  <TimerTile value={timeLeft.s} label="sec" />
                </div>
              )}
            </div>
          ) : (
            <h2 className="text-lg sm:text-xl lg:text-2xl mb-1.5 sm:mb-2 text-black font-semibold">
              {title}
            </h2>
          )}

          <p className="text-sm sm:text-base text-gray-600 leading-5 sm:leading-6">{description}</p>
        </div>
      </div>
      <div className="flex">
        <div className="w-full">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-6 gap-2 md:gap-3 lg:gap-3 ">
            {products?.map((product, index) => (
              <Card key={index} data={product} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PopularProduct;
